import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LayersControl, Marker, ScaleControl, TileLayer, useMap, Popup } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import L, { LatLngTuple } from 'leaflet';
import { isEqual } from 'underscore';
import { makeStyles } from '@mui/styles';

import { companyIcon, depotIcon } from '../../../Common/MapIcons';
import TextPolygon from '../TextPolygon';
import { setSelectedTerritory } from '../../../../actions/territories/pure';
import { reassignPostalCode } from '../../../../actions/territories/thunks';
import { AppDispatch, RootState } from '../../../../index';
import { Address } from '../../../../config/types/Routes';
import { Territory } from '../../../../config/types/Territories';
import { getTerritoryById } from '@/actions/territories/thunks';
import { API_KEY } from '@/config/constants';
import { ChevronRightOutlined, CloseOutlined, SearchOutlined } from '@mui/icons-material';

const { BaseLayer } = LayersControl;

const useStyles = makeStyles((theme: any) => ({
  '@global': {
    '.leaflet-popup-content-wrapper': {
      padding: 0,
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
      border: '1px solid rgba(0,0,0,0.08)',
    },
    '.leaflet-popup-content': {
      margin: 0,
      width: 270
    },
    '.leaflet-popup-tip': {
      background: '#fff',
      border: '1px solid rgba(0,0,0,0.08)',
    },
  },

  popupCard: {
    minWidth: 270,
    background: '#fff',
  },

  popupCardWide: {
    minWidth: 270,
    maxWidth: 272,
    background: '#fff',
  },

  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 36px 20px',
    position: 'relative',
  },

  headerTitle: {
    fontSize: 14,
    color: 'rgba(0, 0, 0, 0.6);',
  },

  headerLight: {
    fontWeight: 400,
    fontSize: 13,
  },

  headerBold: {
    fontWeight: 600,
  },

  closeBtn: {
    position: 'absolute',
    right: 10,
    top: 10,
    width: 28,
    height: 28,
    borderRadius: 8,
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: '#666',
    '&:hover': { background: 'rgba(0,0,0,0.06)' },
  },

  divider: {
    height: 1,
    background: 'rgba(0,0,0,0.10)',
    margin: 0,
  },

  row: {
    padding: '16px 18px',
    fontSize: 16,
    fontWeight: 500,
    color: '#111',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': { background: 'rgba(0,0,0,0.04)' },
  },

  rowRight: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  chevron: {
    color: '#777',
    fontSize: 22,
    lineHeight: 1,
  },

  popupBottomSpace: {
    height: 20,
    background: '#fff',
  },

  subText: {
    padding: '0 18px 12px',
    fontSize: 12,
    color: '#666',
  },

  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '16px 14px',
    background: '#fff',
  },

  searchIcon: {
    color: '#9e9e9e',
    lineHeight: 1,
  },

  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 16,
    color: '#111',
    fontFamily: 'inherit',

    '&::placeholder': {
      color: '#9e9e9e',
    },
  },

  searchDivider: {
    height: 1,
    background: 'rgba(0,0,0,0.12)',
  },

  listWrap: {
    maxHeight: 320,
    overflowY: 'auto',
  },

  colorBar: {
    width: 6,
    height: 50,
    marginRight: 12,
    flex: '0 0 6px',
  },
  rowWithBar: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 16,
    fontWeight: 500,
    color: '#111',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': { background: 'rgba(0,0,0,0.04)' },
  },

}));

type Bounds = { type: 'MultiPolygon'; coordinates: any };
type PostalCode = { id: string; country?: string; code?: string; bounds?: Bounds };

function toLatLng(coords: unknown): LatLngTuple | null {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const lng = Number(coords[0]);
  const lat = Number(coords[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

function getRingsFromBounds(bounds: any): LatLngTuple[][] {
  const mp = bounds?.coordinates;
  if (!Array.isArray(mp)) return [];

  const rings: LatLngTuple[][] = [];

  for (const polygon of mp as any[]) {
    const outerRing = Array.isArray(polygon) && Array.isArray(polygon[0]) ? polygon[0] : null;
    if (!outerRing) continue;

    const latlngs: LatLngTuple[] = [];
    for (const pt of outerRing as any[]) {
      const ll = toLatLng(pt);
      if (ll) latlngs.push(ll);
    }

    if (latlngs.length >= 3) rings.push(latlngs);
  }

  return rings;
}

export const TerritoryMapContent = () => {
  const dispatch: AppDispatch = useDispatch();
  const map = useMap();
  const classes = useStyles();
  const lockMapFitRef = useRef(false);

  const [zipPopup, setZipPopup] = useState<null | {
    latlng: { lat: number; lng: number };
    postalCodeId: string;
    postalLabel: string;
    fromTerritoryId: string;
    step: 'context' | 'reassign';
  }>(null);

  const [reassignSearch, setReassignSearch] = useState('');

  const {
    core: { depots, companyAddress },
    territories: { territories, colors, selected_territory },
  } = useSelector((state: RootState) => state, isEqual);

  const closeZipPopup = () => {
    setZipPopup(null);
    setReassignSearch('');
  };

  const openZipPopup = (e: any, p: PostalCode) => {
    if (!selected_territory?.id) return;
    e?.originalEvent?.preventDefault?.();
    e?.originalEvent?.stopPropagation?.();

    if (!e?.latlng) return;

    setZipPopup({
      latlng: e.latlng,
      postalCodeId: p.id,
      postalLabel: `${p.country ?? ''} ${p.code ?? ''}`.trim() || p.id,
      fromTerritoryId: selected_territory.id,
      step: 'context',
    });
  };

  const openReassign = () => {
    if (!zipPopup) return;
    setReassignSearch('');
    setZipPopup({ ...zipPopup, step: 'reassign' });
  };

  const handleMovePostalCode = async (toTerritoryId: string) => {
    if (!zipPopup) return;

    const sourceTerritoryId = zipPopup.fromTerritoryId;
    lockMapFitRef.current = true;
    try {
      await dispatch(
        reassignPostalCode({
          postalCodeId: zipPopup.postalCodeId,
          fromTerritoryId: zipPopup.fromTerritoryId,
          toTerritoryId,
        }) as any
      );

      await dispatch(getTerritoryById(sourceTerritoryId) as any);
    } finally {
      lockMapFitRef.current = false;
    }
    closeZipPopup();
  };

  const belongsToLabel =
    (selected_territory as any)?.code ??
    (selected_territory as any)?.name ??
    selected_territory?.id ??
    '';

  const filteredTerritories = useMemo(() => {
    const q = reassignSearch.trim().toLowerCase();
    return (territories ?? [])
      .filter((t: Territory) => t.id !== zipPopup?.fromTerritoryId)
      .filter((t: Territory) => {
        if (!q) return true;
        const label = `${String((t as any).name ?? '')} ${String((t as any).code ?? '')}`.toLowerCase();
        return label.includes(q);
      });
  }, [territories, zipPopup?.fromTerritoryId, reassignSearch]);

  const coords = companyAddress?.location?.coordinates;

  const depotLocation =
    Array.isArray(coords) && coords.length === 2
      ? ([coords[1], coords[0]] as [number, number])
      : null;


  const otherDepots = useMemo(() => {
    return (depots ?? [])
      .filter((x: Address) => x?.contactcompany !== companyAddress?.contactcompany)
      .map((x: Address) => toLatLng((x as any)?.location?.coordinates))
      // @ts-ignore
      .filter((x): x is LatLngTuple => Boolean(x));
  }, [depots, companyAddress]);

  const polygons = useMemo(() => {
    const items: React.ReactNode[] = [];

    const pushTerritory = (t: Territory) => {
      const rings = getRingsFromBounds((t as any)?.bounds);

      const hideTerritoryLabel = Boolean(selected_territory);

      rings.forEach((ring, idx) => {
        items.push(
          <TextPolygon
            key={`territory_${t.id}_${idx}`}
            map={map}
            area={t}
            title={
              hideTerritoryLabel
                ? ''
                : (t as any).name ?? (t as any).code ?? t.id
            }
            color={colors?.[t.id]?.color}
            coordinates={ring}
            toggleZip={(area) => {
              dispatch(setSelectedTerritory(undefined));
              dispatch(getTerritoryById(area.id) as any);
            }}
            onContextMenu={() => null}
          />
        );
      });
    };

    const pushPostalCodes = (postalCodes: PostalCode[]) => {
      postalCodes.forEach((p) => {
        const rings = getRingsFromBounds(p.bounds);
        rings.forEach((ring, idx) => {
          items.push(
            <TextPolygon
              key={`postal_${p.id}_${idx}`}
              map={map}
              area={selected_territory}
              title={`${p.country ?? ''} ${p.code ?? ''}`.trim() || p.id}
              color="#333333"
              coordinates={ring}
              toggleZip={() => null}
              onContextMenu={(ev) => openZipPopup(ev, p)}
            />
          );
        });
      });
    };

    if (selected_territory) {
      pushTerritory(selected_territory);
      const postalCodes = (selected_territory as any)?.postal_codes as PostalCode[] | undefined;
      if (Array.isArray(postalCodes) && postalCodes.length) pushPostalCodes(postalCodes);
      return items;
    }

    (territories ?? []).forEach((t: Territory) => pushTerritory(t));
    return items;
  }, [map, dispatch, territories, colors, selected_territory]);

  useEffect(() => {
    if (lockMapFitRef.current) return;
    const points: LatLngTuple[] = [];

    const addBounds = (bounds: any) => {
      getRingsFromBounds(bounds).forEach((ring) => ring.forEach((p) => points.push(p)));
    };

    if (selected_territory) {
      addBounds((selected_territory as any)?.bounds);

      const postalCodes = (selected_territory as any)?.postal_codes as PostalCode[] | undefined;
      if (Array.isArray(postalCodes)) postalCodes.forEach((p) => addBounds(p.bounds));
    } else {
      (territories ?? []).forEach((t: Territory) => addBounds((t as any)?.bounds));
    }

    if (!points.length) return;
    const bounds = L.latLngBounds(points);
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20] });
  }, [map, territories, selected_territory]);

  return (
    <>
      {depotLocation && <Marker position={depotLocation} icon={companyIcon} />}
      {otherDepots.map((coord: any, i: any) => (
        <Marker key={i} position={coord} icon={depotIcon} />
      ))}

      {/* Leaflet Popup (perfect anchor + arrow) */}
      {zipPopup && (
        <Popup
          position={zipPopup.latlng as any}
          closeButton={false}
          closeOnClick={false}
          autoPan
          keepInView
        >
          {zipPopup.step === 'context' ? (
            <div className={classes.popupCard}>
              <div className={classes.headerRow}>
                <div className={classes.headerTitle}>
                  <span className={classes.headerLight}>belongs to </span>
                  <span className={classes.headerBold}>{belongsToLabel}</span>
                </div>
                <div className={classes.closeBtn} onClick={closeZipPopup}>
                  <CloseOutlined fontSize="small" />
                </div>
              </div>

              <div className={classes.divider} />

              <div className={classes.row} onClick={closeZipPopup}>
                Show Details
              </div>

              <div className={classes.divider} />

              <div className={classes.row}
                onClick={(ev) => {
                  ev.preventDefault?.();
                  ev.stopPropagation?.();
                  openReassign();
                }}>
                <div className={classes.rowRight}>
                  <span>Re-assign</span>
                  <span className={classes.chevron}><ChevronRightOutlined fontSize="medium" /></span>
                </div>
              </div>
              <div className={classes.divider} />

              {/* NEW bottom space */}
              <div className={classes.popupBottomSpace} />
            </div>
          ) : (
            <div className={classes.popupCardWide}>
              <div className={classes.headerRow}>
                <div className={classes.headerTitle}>
                  <span className={classes.headerLight}>belongs to </span>
                  <span className={classes.headerBold}>{zipPopup.postalLabel}</span>
                </div>
                <div className={classes.closeBtn} onClick={closeZipPopup}>
                  <CloseOutlined fontSize="small" />
                </div>
              </div>

              <div className={classes.divider} />

              <div className={classes.searchBox}>
                <SearchOutlined fontSize="medium" className={classes.searchIcon} />
                <input
                  className={classes.searchInput}
                  placeholder="Suche..."
                  value={reassignSearch}
                  onChange={(e) => setReassignSearch(e.target.value)}
                />
              </div>

              <div className={classes.divider} />

              <div className={classes.listWrap}>
                <div className={classes.row} onClick={() => handleMovePostalCode('unassign')}>
                  unassign
                </div>

                <div className={classes.divider} />

                {filteredTerritories.map((t: Territory) => (
                  <div
                    key={t.id}
                    onClick={() => handleMovePostalCode(t.id)}
                  >
                    <div className={classes.rowWithBar}>
                      <span
                        className={classes.colorBar}
                        style={{ backgroundColor: colors?.[t.id]?.color || 'transparent' }}
                      />
                      <span>{(t as any).name ?? (t as any).code ?? t.id}</span>
                    </div>
                    <div className={classes.divider} />
                  </div>
                ))}

              </div>
            </div>
          )}
        </Popup>
      )}

      <LayersControl position="topright">
        <BaseLayer checked name="Standard">
          <TileLayer
            url={`https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=lite.day&apiKey=${API_KEY}&lang=de`}
            attribution={`Map © ${new Date().getFullYear()} HERE`}
            subdomains="1234"
            maxZoom={20}
            updateWhenIdle
          />
        </BaseLayer>

        <BaseLayer name="Satellite">
          <TileLayer
            url={`https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=explore.satellite.day&apiKey=${API_KEY}&lang=de`}
            attribution={`Map © ${new Date().getFullYear()} HERE`}
            subdomains="1234"
            maxZoom={20}
            updateWhenIdle
          />
        </BaseLayer>

        <ScaleControl />
        {polygons}
      </LayersControl>
    </>
  );
};
